import { Check, ChevronDown, Languages } from 'lucide-react';

import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { DASHBOARD_LANGUAGES } from '@/features/i18n/dashboardLanguages';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n/context';
import { languageMeta } from '@/lib/i18n/languages';

/**
 * The header's dashboard-language switch.
 *
 * Every language is listed by its own name first - someone looking for Hindi
 * reads हिन्दी, not the English word for it - with the English name beside it.
 * Choosing one goes through `setLanguage`, which `AdminI18nProvider` saves as
 * the merchant's own preference; the store's setting is untouched.
 */
export function AdminLanguageMenu() {
  const { language, direction, setLanguage, t } = useI18n();
  const current = languageMeta(language);

  return (
    <Dropdown
      label={t('nav.language.button', { language: current.nativeName })}
      // The menu opens toward the inside of the page, whichever side that is.
      align={direction === 'rtl' ? 'left' : 'right'}
      triggerClassName="rounded-lg border border-line px-3 py-2 text-ink-soft hover:border-brand-300 hover:bg-white hover:text-brand-700"
      trigger={
        <>
          <Languages className="size-4" aria-hidden />
          <span className="hidden sm:inline">{current.nativeName}</span>
          <ChevronDown className="hidden size-3.5 sm:inline" aria-hidden />
        </>
      }
    >
      {(close) => (
        <>
          <p className="px-3 pb-1 pt-2 text-start text-[11px] font-semibold uppercase tracking-wider text-muted" aria-hidden>
            {t('nav.language.menu')}
          </p>
          {DASHBOARD_LANGUAGES.map((choice) => {
            const selected = choice.code === current.code;
            return (
              <DropdownItem
                key={choice.code}
                role="menuitemradio"
                aria-checked={selected}
                lang={choice.locale}
                onClick={() => {
                  close();
                  if (!selected) setLanguage(choice.code);
                }}
                className={cn('text-start', selected && 'font-medium text-brand-700')}
              >
                <span className="flex-1">
                  {choice.nativeName}
                  {choice.nativeName === choice.name ? null : (
                    <span className="ms-2 text-xs text-muted" lang="en">
                      {choice.name}
                    </span>
                  )}
                </span>
                {selected ? <Check className="size-4 shrink-0" aria-hidden /> : null}
              </DropdownItem>
            );
          })}
        </>
      )}
    </Dropdown>
  );
}
